/**
 * Mathematics extension with code-like behaviour: when the math node is
 * selected (active), show and edit raw LaTeX in the line; when selection
 * leaves, render with KaTeX again.
 *
 * Replaces the default Mathematics node views so users don't need a
 * separate popover to edit formulas.
 */

import { Extension } from '@tiptap/core';
import { NodeSelection, TextSelection, type Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import katex, { type KatexOptions } from 'katex';
import { InlineMath, BlockMath } from '@tiptap/extension-mathematics';
import type { InlineMathOptions } from '@tiptap/extension-mathematics';
import type { BlockMathOptions } from '@tiptap/extension-mathematics';

type SharedKatexOptions = { katexOptions?: KatexOptions };

function createInlineMathNodeView(
  katexOptions: KatexOptions | undefined,
  editor: {
    view: { dispatch: (tr: unknown) => void; dom: HTMLElement };
    state: {
      doc: { nodeAt: (pos: number) => PMNode | null };
      schema: { nodes: { inlineMath?: { create: (attrs: { latex: string }) => PMNode } } };
      selection: unknown;
      tr: unknown;
    };
  },
  getPos: () => number | undefined,
  nodeName: string,
) {
  return ({ node }: { node: PMNode }) => {
    const wrapper = document.createElement('span');
    wrapper.className = 'tiptap-mathematics-render tiptap-mathematics-render--editable';
    wrapper.dataset.type = 'inline-math';
    wrapper.setAttribute('contenteditable', 'false');

    const renderEl = document.createElement('span');
    renderEl.className = 'tiptap-math-render-el';
    const inputEl = document.createElement('input');
    inputEl.type = 'text';
    inputEl.className = 'tiptap-math-input-el';
    inputEl.spellcheck = false;
    inputEl.setAttribute('data-math-input', 'inline');

    wrapper.appendChild(renderEl);
    wrapper.appendChild(inputEl);

    let currentNode = node;

    function renderMath(latex: string) {
      renderEl.textContent = '';
      renderEl.classList.remove('inline-math-error', 'tiptap-math-empty');
      if (!latex.trim()) {
        renderEl.classList.add('tiptap-math-empty');
        return;
      }
      try {
        katex.render(latex, renderEl, { ...katexOptions, throwOnError: false });
      } catch {
        renderEl.textContent = latex;
        renderEl.classList.add('inline-math-error');
      }
    }

    function getNodePos(): number | undefined {
      const pos = getPos();
      if (typeof pos === 'number') return pos;
      const sel = (editor as any).state?.selection;
      if (sel && sel instanceof NodeSelection) return sel.from;
      return undefined;
    }

    function showRenderMode(opts?: { moveCursorAfter?: boolean }) {
      const pos = getNodePos();
      const newLatex = inputEl.value;
      const { state, view } = editor as any;
      let tr: Transaction | null = null;
      if (typeof pos === 'number' && newLatex !== currentNode.attrs.latex) {
        const node = state.doc.nodeAt(pos);
        const nodeType = state.schema.nodes?.inlineMath;
        if (node && nodeType && node.type.name === 'inlineMath') {
          tr = state.tr.setNodeMarkup(pos, nodeType, { ...node.attrs, latex: newLatex });
        }
      }
      if (opts?.moveCursorAfter && typeof pos === 'number') {
        const after = pos + currentNode.nodeSize;
        const baseTr = (tr ?? state.tr) as Transaction;
        const doc = baseTr.doc;
        const sel = TextSelection.create(doc, after);
        tr = baseTr.setSelection(sel);
      }
      if (tr) view.dispatch(tr);
      // Hide input after dispatch so blur handler doesn't run and overwrite selection
      inputEl.style.display = 'none';
      renderEl.style.display = '';
      renderMath(newLatex);
      if (opts?.moveCursorAfter && typeof pos === 'number') {
        const after = pos + currentNode.nodeSize;
        // Use Tiptap's chain API to set selection and focus together, ensuring DOM selection matches ProseMirror state
        const tiptapEditor = (editor as any).tiptapEditor || (editor as any).editor;
        if (tiptapEditor && typeof tiptapEditor.chain === 'function') {
          tiptapEditor.chain().setTextSelection(after).focus().run();
        } else {
          // Fallback: use ProseMirror view.focus() if available, or dom.focus()
          const view = (editor as any).view;
          if (view && typeof view.focus === 'function') {
            view.focus();
          } else {
            const dom = (editor as any).view?.dom;
            if (dom) dom.focus();
          }
        }
      }
    }

    function showEditMode() {
      renderEl.style.display = 'none';
      inputEl.style.display = 'inline';
      inputEl.value = currentNode.attrs.latex ?? '';
      inputEl.select();
      setTimeout(() => inputEl.focus(), 0);
    }

    function isThisNodeSelected(): boolean {
      const pos = getPos();
      if (typeof pos !== 'number') return false;
      const sel = editor.state.selection;
      return sel instanceof NodeSelection && sel.from === pos;
    }

    function checkSelection() {
      const pos = getPos();
      const selected = isThisNodeSelected();
      const sel = editor.state.selection as { from: number; constructor?: { name: string } };
      const inputHidden = inputEl.style.display === 'none';
      // #region agent log
      (() => {
        const payload = { location: 'mathematics-inline-edit.ts:checkSelection(inline)', message: 'checkSelection', data: { pos, selected, selFrom: sel.from, selType: sel.constructor?.name, inputHidden }, timestamp: Date.now(), hypothesisId: selected ? 'H4' : 'H3' };
        fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }).catch(() => {});
      })();
      // #endregion
      if (typeof pos !== 'number') return;
      if (selected) {
        if (inputHidden) {
          showEditMode();
        }
      } else {
        if (!inputHidden) {
          showRenderMode();
        }
      }
    }

    // Click handler to enter edit mode
    wrapper.addEventListener('click', (e) => {
      if (inputEl.style.display === 'none') {
        e.preventDefault();
        e.stopPropagation();
        const pos = getPos();
        if (typeof pos === 'number') {
          const { state, view } = editor as any;
          const tr = state.tr as Transaction;
          const nodeSelection = NodeSelection.create(tr.doc, pos);
          view.dispatch(tr.setSelection(nodeSelection));
          showEditMode();
        }
      }
    });

    renderMath(currentNode.attrs.latex ?? '');
    inputEl.style.display = 'none';

    inputEl.addEventListener('blur', () => {
      showRenderMode();
    });

    const focusEditorDom = () => {
      const dom = (editor as any).view?.dom;
      if (dom) dom.focus();
    };

    // Prevent all key and input events from reaching ProseMirror so typing in the input doesn't replace the node
    const stopProp = (e: Event) => {
      e.stopPropagation();
    };
    inputEl.addEventListener('keydown', stopProp, true);
    inputEl.addEventListener('keypress', stopProp, true);
    inputEl.addEventListener('input', stopProp, true);
    // Handle Enter/Escape in capture phase so they run before stopProp and we can prevent default and persist
    inputEl.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') {
          inputEl.value = currentNode.attrs.latex ?? '';
          focusEditorDom();
          showRenderMode();
          e.preventDefault();
          e.stopPropagation();
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          showRenderMode({ moveCursorAfter: true });
        }
      },
      true,
    );

    // Listen to ProseMirror's selection changes via transaction
    const handleUpdate = () => {
      checkSelection();
    };

    // Store cleanup function
    let cleanup: (() => void) | null = null;

    // Try to use Tiptap's selectionUpdate if available (for Tiptap editor wrapper)
    const tiptapEditor = (editor as any).tiptapEditor || (editor as any).editor;
    if (tiptapEditor && typeof tiptapEditor.on === 'function') {
      tiptapEditor.on('selectionUpdate', handleUpdate);
      cleanup = () => {
        if (tiptapEditor && typeof tiptapEditor.off === 'function') {
          tiptapEditor.off('selectionUpdate', handleUpdate);
        }
      };
    }

    // Defer DOM focus listener until view is mounted (node view can run before editor.view is available)
    let editorDomRef: HTMLElement | null = null;
    const attachFocusListener = () => {
      const dom = (editor as any).view?.dom;
      if (!dom || editorDomRef === dom) return;
      editorDomRef = dom;
      const handleFocus = () => checkSelection();
      dom.addEventListener('focus', handleFocus);
      const prevCleanup = cleanup;
      cleanup = () => {
        prevCleanup?.();
        dom.removeEventListener('focus', handleFocus);
        editorDomRef = null;
      };
    };
    setTimeout(attachFocusListener, 0);

    // Initial check (after view may be mounted)
    setTimeout(() => {
      checkSelection();
    }, 0);

    return {
      dom: wrapper,
      update(updatedNode: PMNode) {
        if (updatedNode.type.name !== nodeName) return false;
        currentNode = updatedNode;
        if (inputEl.style.display !== 'none') {
          inputEl.value = currentNode.attrs.latex ?? '';
        } else {
          renderMath(currentNode.attrs.latex ?? '');
        }
        checkSelection();
        return true;
      },
      destroy() {
        if (cleanup) cleanup();
      },
    };
  };
}

function createBlockMathNodeView(
  katexOptions: KatexOptions | undefined,
  editor: {
    view: { dispatch: (tr: unknown) => void; dom: HTMLElement };
    state: { selection: unknown; tr: unknown };
    commands: { updateBlockMath: (opts: { latex: string; pos: number }) => boolean };
  },
  getPos: () => number | undefined,
  nodeName: string,
) {
  return ({ node }: { node: PMNode }) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'tiptap-mathematics-render tiptap-mathematics-render--editable';
    wrapper.dataset.type = 'block-math';
    wrapper.setAttribute('contenteditable', 'false');

    const renderEl = document.createElement('div');
    renderEl.className = 'block-math-inner tiptap-math-render-el';
    const inputEl = document.createElement('textarea');
    inputEl.className = 'tiptap-math-input-el tiptap-math-block-input';
    inputEl.rows = 3;
    inputEl.spellcheck = false;
    inputEl.setAttribute('data-math-input', 'block');

    wrapper.appendChild(renderEl);
    wrapper.appendChild(inputEl);

    let currentNode = node;

    function renderMath(latex: string) {
      renderEl.textContent = '';
      renderEl.classList.remove('block-math-error', 'tiptap-math-empty');
      if (!latex.trim()) {
        renderEl.classList.add('tiptap-math-empty');
        return;
      }
      try {
        katex.render(latex, renderEl, { ...katexOptions, throwOnError: false, displayMode: true });
      } catch {
        renderEl.textContent = latex;
        renderEl.classList.add('block-math-error');
      }
    }

    function showRenderMode() {
      const pos = getPos();
      if (typeof pos !== 'number') return;
      const newLatex = inputEl.value;
      inputEl.style.display = 'none';
      renderEl.style.display = '';
      if (newLatex !== currentNode.attrs.latex) {
        (editor.commands as any).updateBlockMath({ latex: newLatex, pos });
      }
      renderMath(newLatex);
    }

    function showEditMode() {
      renderEl.style.display = 'none';
      inputEl.style.display = 'block';
      inputEl.value = currentNode.attrs.latex ?? '';
      inputEl.focus();
      inputEl.select();
    }

    function isThisNodeSelected(): boolean {
      const pos = getPos();
      if (typeof pos !== 'number') return false;
      const sel = editor.state.selection;
      return sel instanceof NodeSelection && sel.from === pos;
    }

    function checkSelection() {
      const pos = getPos();
      if (typeof pos !== 'number') return;
      if (isThisNodeSelected()) {
        if (inputEl.style.display === 'none') {
          showEditMode();
        }
      } else {
        if (inputEl.style.display !== 'none') {
          showRenderMode();
        }
      }
    }

    // Click handler to enter edit mode
    wrapper.addEventListener('click', (e) => {
      if (inputEl.style.display === 'none') {
        e.preventDefault();
        e.stopPropagation();
        const pos = getPos();
        if (typeof pos === 'number') {
          const { state, view } = editor as any;
          const tr = state.tr as Transaction;
          const nodeSelection = NodeSelection.create(tr.doc, pos);
          view.dispatch(tr.setSelection(nodeSelection));
          showEditMode();
        }
      }
    });

    renderMath(currentNode.attrs.latex ?? '');
    inputEl.style.display = 'none';

    inputEl.addEventListener('blur', () => {
      setTimeout(() => {
        if (!isThisNodeSelected()) {
          showRenderMode();
        }
      }, 100);
    });

    const focusEditorDom = () => {
      const dom = (editor as any).view?.dom;
      if (dom) dom.focus();
    };

    const stopProp = (e: Event) => e.stopPropagation();
    inputEl.addEventListener('keydown', stopProp, true);
    inputEl.addEventListener('keypress', stopProp, true);
    inputEl.addEventListener('input', stopProp, true);
    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        inputEl.value = currentNode.attrs.latex ?? '';
        focusEditorDom();
        showRenderMode();
        e.preventDefault();
      }
    });

    // Listen to ProseMirror's selection changes via transaction
    const handleUpdate = () => {
      checkSelection();
    };

    // Store cleanup function
    let cleanup: (() => void) | null = null;

    // Try to use Tiptap's selectionUpdate if available (for Tiptap editor wrapper)
    const tiptapEditor = (editor as any).tiptapEditor || (editor as any).editor;
    if (tiptapEditor && typeof tiptapEditor.on === 'function') {
      tiptapEditor.on('selectionUpdate', handleUpdate);
      cleanup = () => {
        if (tiptapEditor && typeof tiptapEditor.off === 'function') {
          tiptapEditor.off('selectionUpdate', handleUpdate);
        }
      };
    }

    // Defer DOM focus listener until view is mounted (node view can run before editor.view is available)
    let editorDomRef: HTMLElement | null = null;
    const attachFocusListener = () => {
      const dom = (editor as any).view?.dom;
      if (!dom || editorDomRef === dom) return;
      editorDomRef = dom;
      const handleFocus = () => checkSelection();
      dom.addEventListener('focus', handleFocus);
      const prevCleanup = cleanup;
      cleanup = () => {
        prevCleanup?.();
        dom.removeEventListener('focus', handleFocus);
        editorDomRef = null;
      };
    };
    setTimeout(attachFocusListener, 0);

    // Initial check (after view may be mounted)
    setTimeout(() => {
      checkSelection();
    }, 0);

    return {
      dom: wrapper,
      update(updatedNode: PMNode) {
        if (updatedNode.type.name !== nodeName) return false;
        currentNode = updatedNode;
        if (inputEl.style.display !== 'none') {
          inputEl.value = currentNode.attrs.latex ?? '';
        } else {
          renderMath(currentNode.attrs.latex ?? '');
        }
        checkSelection();
        return true;
      },
      destroy() {
        if (cleanup) cleanup();
      },
    };
  };
}

const defaultKatexOptions: KatexOptions = { throwOnError: false };

export type MathematicsInlineEditOptions = {
  katexOptions?: KatexOptions;
  inlineOptions?: Omit<InlineMathOptions, 'katexOptions'>;
  blockOptions?: Omit<BlockMathOptions, 'katexOptions'>;
};

const InlineMathInlineEdit = InlineMath.extend<SharedKatexOptions>({
  addNodeView() {
    const katexOptions = this.options.katexOptions ?? defaultKatexOptions;
    const editor = this.editor;
    return ({ node, getPos }: { node: PMNode; getPos: () => number | undefined }) =>
      createInlineMathNodeView(katexOptions, editor as any, getPos, 'inlineMath')({ node });
  },

  addCommands() {
    return {
      // Allow empty latex so "Insert math" opens an empty inline edit field
      insertInlineMath:
        (options: { latex?: string; pos?: number }) =>
        (props: { editor: { state: { selection: { from: number } } }; tr: Transaction }) => {
          const latex = options?.latex ?? '';
          const from = options?.pos ?? props.editor.state.selection.from;
          props.tr.replaceWith(from, from, this.type.create({ latex }));
          return true;
        },
    };
  },
});

const BlockMathInlineEdit = BlockMath.extend<SharedKatexOptions>({
  addNodeView() {
    const katexOptions = this.options.katexOptions ?? defaultKatexOptions;
    const editor = this.editor;
    return ({ node, getPos }: { node: PMNode; getPos: () => number | undefined }) =>
      createBlockMathNodeView(katexOptions, editor as any, getPos, 'blockMath')({ node });
  },
});

export const MathematicsInlineEdit = Extension.create<MathematicsInlineEditOptions>({
  name: 'MathematicsInlineEdit',

  addOptions() {
    return {
      katexOptions: undefined,
      inlineOptions: undefined,
      blockOptions: undefined,
    };
  },

  addExtensions() {
    const katexOpts = this.options.katexOptions ?? defaultKatexOptions;
    return [
      BlockMathInlineEdit.configure({
        ...this.options.blockOptions,
        katexOptions: katexOpts,
      }),
      InlineMathInlineEdit.configure({
        ...this.options.inlineOptions,
        katexOptions: katexOpts,
      }),
    ];
  },
});

export default MathematicsInlineEdit;
